/*
  * Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
  * SPDX-License-Identifier: MIT
  *
  * Licensed under the MIT License. See the LICENSE accompanying this file
  * for the specific language governing permissions and limitations under
  * the License.
  */

import React from 'react';

import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CssBaseline from '@material-ui/core/CssBaseline';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import TabSignInData from './tabSignInData';
import { applyBrand, getCurrentBrand } from '../../utils/applyBrand';
import useWindowDimensions from '../../components/ViewPort/useWindowDimensions';
import { authShellContentCentered } from '../../styles/authPageShell';

const useStyles = makeStyles((theme) => ({
	root: {
		...authShellContentCentered(theme),
		flexGrow: 1,
		backgroundColor: '#ffffff',
	},
	// ログインフォーム列 (28.75rem) + CardContent 左右 padding 40px。PC ではこの幅、狭い画面では 100%
	pageColumn: {
		boxSizing: 'border-box',
		width: '100%',
		maxWidth: 'calc(28.75rem + 80px)',
		marginLeft: 'auto',
		marginRight: 'auto',
	},
	gridLogo: {
		paddingBottom: theme.spacing(3),
		width: '100%',
	},
	grid: {
		justifyContent: 'center',
		width: '100%',
	},
	cardItem: {
		width: '100%',
		maxWidth: '100%',
		flexBasis: '100%',
	},
	card: {
		justifyContent: 'center',
		width: '100%',
		maxWidth: '100%',
	},
	cardContent: {
		justifyContent: 'center',
		textAlign: 'left',
		'&:last-child': {
			paddingBottom: theme.spacing(1),
		},
	},
	brandLogo: {
		maxWidth: 300,
		height: 'auto',
	},
	settingsInner: {
		textAlign: 'left',
		width: '100%',
	},
}));

export default function Content(props) {
	const classes = useStyles();
	const { width } = useWindowDimensions();
	const brand = getCurrentBrand();
	const isEvents = brand === 'halmekEvents';
	const logoSrc = isEvents ? '/assets/img/halmekevents_logo.svg' : '/assets/img/halmekup_logo.svg';
	const logoAlt = isEvents ? 'HALMEK events' : 'HALMEK up（ハルメクアップ）';
	const isFocusedSettingsFlow = ['email', 'phone', 'password'].includes(props.mode);

	// ログイン画面と同じ白背景（MUI CssBaseline の body 既定色との差を解消）
	React.useEffect(() => {
		const html = document.documentElement;
		const body = document.body;
		const prevHtml = html.style.backgroundColor;
		const prevBody = body.style.backgroundColor;
		html.style.backgroundColor = '#ffffff';
		body.style.backgroundColor = '#ffffff';
		return () => {
			html.style.backgroundColor = prevHtml;
			body.style.backgroundColor = prevBody;
		};
	}, []);

	// index.js の初回 applyBrand より後に client_id が確定するケースで body クラスと halmek-custom を同期する
	React.useEffect(() => {
		const id = props.clientId?.trim();
		if (id) {
			localStorage.setItem('client-id', id);
		}
		applyBrand();
	}, [props.clientId]);

	return (
		<div className={classes.root}>
			<CssBaseline />
			<div className={classes.pageColumn}>
				<Grid
					container
					spacing={0}
					direction="column"
					alignItems="center"
					justify="center"
					className={classes.gridLogo}
				>
					<Typography variant={width < 550 ? 'h4' : 'h3'} color="primary">
						<img className={classes.brandLogo} src={logoSrc} alt={logoAlt} />
					</Typography>
				</Grid>
				<Grid container className={classes.grid}>
					<Grid item xs={12} className={classes.cardItem}>
						<Card variant="outlined" className={classes.card}>
							<CardContent className={classes.cardContent}>
								<div className="login-form-column">
									<Box className={classes.settingsInner}>
										<TabSignInData
											reloadUserData={props.reloadUserData}
											mode={props.mode}
											redirectUri={props.redirectUri}
											clientState={props.clientState}
											clientId={props.clientId}
										/>
										{isFocusedSettingsFlow && (
											<div className="login-footer-pmark">
												<img src="/assets/img/ico-pmark.png" alt="プライバシーマーク" />
											</div>
										)}
									</Box>
								</div>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</div>
		</div>
	);
}
